/**
 * @file       Grid vuex store, used by the whole grid module.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import axios from "@/plugins/axios.js";

/**
 * Gets the default state.
 *
 * @return  {Object}  The default state.
 */
function getDefaultState() {
  return {
    comment: {
      status: null,
      data: { data: [] },
      error: null,
      updated: {
        status: null,
        data: { data: [] },
        error: null
      },
      created: {
        status: null,
        data: { data: [] },
        error: null
      },
      criteria: {
        status: null,
        data: { data: [] },
        error: null
      }
    },
    reference: {
      status: null,
      data: { data: [] },
      error: null,
      updated: {
        status: null,
        data: { data: [] },
        error: null
      },
      created: {
        status: null,
        data: { data: [] },
        error: null
      }
    },
    assign: {
      status: null,
      data: null,
      error: null
    },
    currentGrid: {}
  };
}

/**
 * Grid vuex actions.
 *
 * @type  {Object}
 */
const actions = {
  /**
   * Get the grid.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  getGrid: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("getGrid");

      axios
        .get(`teacher/grid`, { params })
        .then(response => {
          commit("getGridSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("getGridCommentError", error);
          reject(error);
        });
    });
  },

  /**
   * Put the grid comment.
   *
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Function}  arg1.dispatch  Dispatch the action
   * @param   {Object}    params         The parameters
   *
   * @return  {Promise}
   */
  putGridComment: ({ commit, dispatch }, params) => {
    return new Promise((resolve, reject) => {
      commit("putGridComment");

      axios
        .put(`teacher/grid/commentCriteria`, params)
        .then(response => {
          commit("putGridCommentSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("putGridCommentError", error);
          dispatch("getGrid");
          reject(error);
        });
    });
  },

  /**
   * Put the grid reference.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  putGridReference: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("putGridReference");

      axios
        .put(`teacher/grid/referenceCriteria`, params)
        .then(response => {
          commit("putGridReferenceSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("putGridReferenceError", error);
          reject(error);
        });
    });
  },

  /**
   * Put comment grade.
   *
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Function}  arg1.dispatch  Dispatch the action
   * @param   {Object}    params         The parameters
   *
   * @return  {Promise}
   */
  putCommentGrade: ({ commit, dispatch }, params) => {
    return new Promise((resolve, reject) => {
      commit("putCommentGrade");

      axios
        .put(`teacher/grid/commentGrade`, params)
        .then(response => {
          commit("putCommentGradeSuccess", response);
          dispatch("getGrid");
          resolve(response);
        })
        .catch(error => {
          commit("putCommentGradeError", error);
          dispatch("getGrid");
          reject(error);
        });
    });
  },

  /**
   * Post the grid comment.
   *
   * @param   {Function}  arg1.commit     Commit the action
   * @param   {Function}  arg1.dispatch   Dispatch the action
   * @param   {Object}    payload         The payload
   *
   * @return  {Promise}
   */
  postGridComment: ({ commit, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      commit("postGridComment");

      axios
        .post(`teacher/grid/commentGrade`, payload)
        .then(response => {
          commit("postGridCommentSuccess", response);
          dispatch("getGrid");
          resolve(response);
        })
        .catch(error => {
          commit("postGridCommentError", error);
          reject(error);
        });
    });
  },

  /**
   * Post the grid comment criteria.
   *
   * @param   {Function}  arg1.commit     Commit the action
   * @param   {Function}  arg1.dispatch   Dispatch the action
   * @param   {Object}    payload         The payload
   *
   * @return  {Promise}
   */
  postGridCommentCriteria: ({ commit, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      commit("postGridCommentCriteria");

      axios
        .post(`teacher/grid/commentCriteria`, payload)
        .then(response => {
          commit("postGridCommentCriteriaSuccess", response);
          dispatch("getGrid");
          resolve(response);
        })
        .catch(error => {
          commit("postGridCommentCriteriaError", error);
          dispatch("getGrid");
          reject(error);
        });
    });
  },

  /**
   * Post the grid reference criteria.
   *
   * @param   {Function}  arg1.commit     Commit the action
   * @param   {Object}    payload         The payload
   *
   * @return  {Promise}
   */
  postGridRefCriteria: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("postGridRefCriteria");

      axios
        .post(`teacher/grid/referenceCriteria`, payload)
        .then(response => {
          commit("postGridRefCriteriaSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("postGridRefCriteriaError", error);
          reject(error);
        });
    });
  },

  /**
   * Post the grid assign.
   *
   * @param   {Function}  arg1.commit     Commit the action
   * @param   {Function}  arg1.dispatch   Dispatch the action
   * @param   {Object}    payload         The payload
   *
   * @return  {Promise}
   */
  postGridAssign: ({ commit, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      commit("postGridAssign");

      axios
        .post(`teacher/grid/assign`, payload)
        .then(response => {
          commit("postGridAssignSuccess", response);
          dispatch("getGrid");
          resolve(response);
        })
        .catch(error => {
          commit("postGridAssignError", error);
          dispatch("getGrid");
          reject(error);
        });
    });
  }
};

/**
 * Grid vuex mutations.
 *
 * @type  {Object}
 */
const mutations = {
  /**
   * Get grid.
   *
   * @param  {Object}  state   The vuex state
   */
  getGrid: state => {
    state.comment.status = "fetching";
    state.comment.error = {};
  },

  /**
   * Get grid success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  getGridSuccess: (state, { data }) => {
    state.comment.status = "success";
    state.comment.error = null;
    state.comment.data = data;
  },

  /**
   * Get grid error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  getGridError: (state, err) => {
    state.comment.status = "error";
    state.comment.error = err;
  },

  /**
   * Get grid comment.
   *
   * @param  {Object}  state   The vuex state
   */
  putGridComment: state => {
    state.comment.updated.status = "fetching";
    state.comment.updated.error = {};
  },

  /**
   * Put grid comment success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  putGridCommentSuccess: (state, { data }) => {
    state.comment.updated.status = "success";
    state.comment.updated.error = null;
    state.comment.updated.data = data;
  },

  /**
   * Get grid comment error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  putGridCommentError: (state, err) => {
    state.comment.updated.status = "error";
    state.comment.updated.error = err;
  },

  /**
   * Put comment grade.
   *
   * @param  {Object}  state   The vuex state
   */
  putCommentGrade: state => {
    state.comment.updated.status = "fetching";
    state.comment.updated.error = {};
  },

  /**
   * Put comment grade success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  putCommentGradeSuccess: (state, { data }) => {
    state.comment.updated.status = "success";
    state.comment.updated.error = null;
    state.comment.updated.data = data;
  },

  /**
   * Put comment grade error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  putCommentGradeError: (state, err) => {
    state.comment.updated.status = "error";
    state.comment.updated.error = err;
  },

  /**
   * Put grid reference.
   *
   * @param  {Object}  state   The vuex state
   */
  putGridReference: state => {
    state.reference.updated.status = "fetching";
    state.reference.updated.error = {};
  },

  /**
   * Put grid reference success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  putGridReferenceSuccess: (state, { data }) => {
    state.reference.updated.status = "success";
    state.reference.updated.error = null;
    state.reference.updated.data = data;
  },

  /**
   * Put grid reference error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  putGridReferenceError: (state, err) => {
    state.reference.updated.status = "error";
    state.reference.updated.error = err;
  },

  /**
   * Post grid comment.
   *
   * @param  {Object}  state   The vuex state
   */
  postGridComment: state => {
    state.comment.created.status = "fetching";
    state.comment.created.error = {};
  },

  /**
   * Post grid comment success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  postGridCommentSuccess: (state, { data }) => {
    state.comment.created.status = "success";
    state.comment.created.error = null;
    state.comment.created.data = data;
  },

  /**
   * Post grid comment error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  postGridCommentError: (state, err) => {
    state.comment.created.status = "error";
    state.comment.created.error = err;
  },

  /**
   * Post grid comment criteria.
   *
   * @param  {Object}  state   The vuex state
   */
  postGridCommentCriteria: state => {
    state.comment.criteria.status = "fetching";
    state.comment.criteria.error = {};
  },

  /**
   * Get grid comment criteria success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  postGridCommentCriteriaSuccess: (state, { data }) => {
    state.comment.criteria.status = "success";
    state.comment.criteria.error = null;
    state.comment.criteria.data = data;
  },

  /**
   * Post grid comment criteria error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  postGridCommentCriteriaError: (state, err) => {
    state.comment.criteria.status = "error";
    state.comment.criteria.error = err;
  },

  /**
   * Post grid reference criteria.
   *
   * @param  {Object}  state   The vuex state
   */
  postGridRefCriteria: state => {
    state.reference.criteria.status = "fetching";
    state.reference.criteria.error = {};
  },

  /**
   * Post grid reference criteria success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  postGridRefCriteriaSuccess: (state, { data }) => {
    state.reference.criteria.status = "success";
    state.reference.criteria.error = null;
    state.reference.criteria.data = data;
  },

  /**
   * Post grid reference criteria error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  postGridRefCriteriaError: (state, err) => {
    state.reference.criteria.status = "error";
    state.reference.criteria.error = err;
  },

  /**
   * Post grid assign.
   *
   * @param  {Object}  state   The vuex state
   */
  postGridAssign: state => {
    state.assign.status = "fetching";
    state.assign.error = {};
  },

  /**
   * Post grid assign success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  postGridAssignSuccess: (state, { data }) => {
    state.assign.status = "success";
    state.assign.error = null;
    state.assign.data = data;
  },

  /**
   * Post grid assign error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  postGridAssignError: (state, err) => {
    state.assign.status = "error";
    state.assign.error = err;
  },

  /**
   * Set current grid.
   *
   * @param  {Object}  state      The vuex state
   * @param  {Object}  payload    The payload
   */
  setCurrentGrid: (state, payload) => {
    state.currentGrid = payload;
  }
};

/**
 * Export the store.
 *
 * @type  {Object}
 */
export default {
  namespaced: true,
  state: getDefaultState(),
  actions,
  mutations
};
